import { cevir } from "@/lib/dil";

/**
 * D-069: rework raporunun REJECT'li ek doğrulama (ADDITIONAL) satırı; ekranın saf yardımcıları.
 *
 * REJECT'li ek satır komponentinde bekleyen bir değişim kalemi olarak açılır (numarası riserde 9001'den başlar); kalemi henüz
 * açılmamış satır komponenti tamamlatmaz ve assembly'e aktartmaz. Sayım, "tamam" ve "aktarılabilir" SUNUCUDA hesaplanır
 * (muayene.controller.ts `komponentDurumu`); buradaki fonksiyonlar yalnız sunucunun döndürdüğü alanları ekran metnine ve tona
 * çevirir: rozet, çipler, riser bandı, onay penceresi ve sonuç bandı. JSX yok; tarih biçimi dışarıdan verilir.
 *
 * Cümleler `cevir("… {0} …", deger)` ile kurulur; Türkçeleri docs/ceviri/tr-elle.tsv'de `# U-063 (D-069)` başlığı altındadır.
 * Excel'den birebir gelen değerler (komponent adı, Rework item, Name, Result, Evidence) çevrilmez.
 */

export type EkTon = "neutral" | "ok" | "warn" | "bad";

/** Kalem kartındaki karar çipinin tonu (Item level Triage'ın var olan kuralı): reject / replace kırmızı, rework sarı, accept yeşil, ötekiler gri. */
export const kararTonu = (karar: string): EkTon =>
  /reject|replace/i.test(karar) ? "bad" : /rework/i.test(karar) ? "warn" : /accept/i.test(karar) ? "ok" : "neutral";

/** Ek doğrulama satırının Result çipi: metinde "conflict" geçiyorsa sarı, geçmiyorsa karar çipinin kuralı (sabit yeşil değil). */
export const ekSonucTonu = (sonuc: string): EkTon => (/conflict/i.test(sonuc) ? "warn" : kararTonu(sonuc));

/**
 * Ek REJECT satırından açılmış kalemin mor çipi: raporun kendi item numarası ("Rework report · Item 22"); Rework item hücresi boşsa
 * "Rework report · additional row". Öteki kalemlerde null (çip çizilmez).
 */
export function ekKalemEtiketi(x: { ekDogrulama?: boolean | null; ekItem?: string | null }): string | null {
  if (!x.ekDogrulama) return null;
  return x.ekItem && x.ekItem.trim() ? cevir("Rework report · Item {0}", x.ekItem) : cevir("Rework report · additional row");
}

/** Ek REJECT kaleminde ilk muayenenin önerilen iş ve bulgu alanları boştur; alıntı satırın Evidence metnine düşer. */
export const ekAlintisi = (x: { ekDogrulama?: boolean | null; onerilenIs: string | null; bulgu: string | null; kaynakNot: string | null }): string | null =>
  x.onerilenIs ?? x.bulgu ?? (x.ekDogrulama ? x.kaynakNot : null);

/** Sunucunun komponent sayımından bu ekranın okuduğu iki alan (eski yanıtta yoksa 0 sayılır). */
type EkSayim = { ekReject?: number; ekBekleyen?: number };

/**
 * Komponent rozetinin D-069 dalları; var olan dallardan ÖNCE sorulur, null dönerse var olan dallar sürer. Hepsi kırmızıdır.
 * - Kalemi açılmamış REJECT satırı: aktarılmış komponentte "transferred to assembly · re-opened: n additional REJECT waiting"
 *   (n = açılmamış satırlar + onlardan açılmış bekleyen kalemler), aktarılmamışta "n additional REJECT not opened · cannot be transferred".
 * - Aktarılmış komponentte bekleyen ek REJECT kalemi: aynı "re-opened" rozeti; kalem Repair planning'e gönderilince yeşile döner.
 */
export function ekRozeti(k: { aktarildi: boolean; sayim: EkSayim }): { metin: string; ton: "bad" } | null {
  const acik = k.sayim.ekReject ?? 0;
  const bekleyen = k.sayim.ekBekleyen ?? 0;
  if (acik > 0) {
    return k.aktarildi
      ? { metin: cevir("transferred to assembly · re-opened: {0} additional REJECT waiting", acik + bekleyen), ton: "bad" }
      : { metin: cevir("{0} additional REJECT not opened · cannot be transferred", acik), ton: "bad" };
  }
  if (k.aktarildi && bekleyen > 0) return { metin: cevir("transferred to assembly · re-opened: {0} additional REJECT waiting", bekleyen), ton: "bad" };
  return null;
}

/** Çubuğun altındaki sayım cümlesine eklenen parça (" · 1 additional REJECT not opened"); açılmamış satır yoksa boş. */
export const ekCubukAlti = (sayim: EkSayim): string => ((sayim.ekReject ?? 0) > 0 ? cevir(" · {0} additional REJECT not opened", sayim.ekReject) : "");

/** Riser listesindeki (Inspection verification) kırmızı çipin metni. */
export const ekListeCipi = (n: number): string => cevir("{0} additional REJECT not opened", n);

/** Komponentin içinde, düğmelerin üstündeki kırmızı bant: kalemi açılmamış REJECT satırı varken. */
export const ekAcilmamisCumlesi = (n: number): string =>
  cevir("{0} REJECT row of the rework report is not opened as an Item; the component cannot be completed or transferred to assembly. Open it with “Open as Replacement Items” in the riser box.", n);

/** Aynı yerdeki sarı bant: aktarılmış komponentte ek REJECT kalemi beklerken. */
export const ekYenidenAcikCumlesi = (n: number): string =>
  cevir("This component is already transferred to assembly and is open again: {0} additional REJECT Item is waiting. The transfer and everything recorded so far are kept; the component is complete again when the Item is sent to Repair planning.", n);

/** Ek doğrulama satırının sunucudan gelen D-069 alanları: `reject` sunucuda hesaplanır, `kalemId` doluysa satır o kalem olarak işlenir. */
type EkSatiri = { item: string | null; ad: string; reject?: boolean; kalemId?: string | null };

/**
 * Ek doğrulama bloğunda satırın ikinci çipi: kalemi açılmış satırda gri "handled as #9001 under Replacement Items" (numara komponentin
 * kalemleri arasında bulunur), kalemi açılmamış REJECT satırında kırmızı "not opened as an Item yet"; öteki satırlarda yok.
 */
export function ekSatirCipi(e: EkSatiri, kalemler: readonly { id: string; no: number }[]): { metin: string; ton: "neutral" | "bad" } | null {
  if (e.kalemId) {
    const kalem = kalemler.find((x) => x.id === e.kalemId);
    return kalem ? { metin: cevir("handled as #{0} under Replacement Items", kalem.no), ton: "neutral" } : null;
  }
  return e.reject ? { metin: cevir("not opened as an Item yet"), ton: "bad" } : null;
}

/** Kalemi açılmamış REJECT satırı, komponentiyle. */
export interface EkAcikSatir {
  kod: string;
  komponent: string;
  aktarildi: boolean;
  item: string | null;
  ad: string;
}

/** Verilen (görünür) komponentlerin kalemi açılmamış REJECT satırları: komponent sırasında, komponent içinde satır sırasında. */
export function ekAcikSatirlar(komponentler: readonly { kod: string; ad: string; aktarildi: boolean; ekDogrulamalar: readonly EkSatiri[] }[]): EkAcikSatir[] {
  return komponentler.flatMap((k) =>
    k.ekDogrulamalar.filter((e) => e.reject && !e.kalemId).map((e) => ({ kod: k.kod, komponent: k.ad, aktarildi: k.aktarildi, item: e.item, ad: e.ad })),
  );
}

/**
 * Satırın adı. Onay penceresinin listesinde "Main Tube · Item 13 · Main tube - post sand blasting" (`itemli`; Rework item hücresi boşsa
 * "Item …" yazılmaz); riser bandında "Main Tube · Main tube - post sand blasting".
 */
export const ekSatirAdi = (s: Pick<EkAcikSatir, "komponent" | "item" | "ad">, itemli: boolean): string =>
  itemli && s.item && s.item.trim() ? cevir("{0} · Item {1} · {2}", s.komponent, s.item, s.ad) : cevir("{0} · {1}", s.komponent, s.ad);

/** Riser bandının kalın cümlesi (tekil / çoğul). */
export const ekBantBasligi = (n: number): string =>
  n === 1 ? cevir("{0} REJECT row of the rework report is not opened as an Item.", n) : cevir("{0} REJECT rows of the rework report are not opened as Items.", n);

/** Riser bandının devamı: hangi komponentin hangi satırı (birden çok satır "; " ile ayrılır). */
export const ekBantAciklamasi = (satirlar: readonly EkAcikSatir[]): string =>
  cevir("It orders a repair, but no repair job can be opened for it yet: {0}. The component cannot be completed or transferred to assembly until the row is opened and sent to Repair planning.", satirlar.map((s) => ekSatirAdi(s, false)).join("; "));

/** Riserin assembly kuyruğu kaydı (sayfa isteğinin `kuyruk` alanı); kuyrukta değilse null. `raporVar`: Coating ya da Assembly raporu yüklü. */
export interface EkKuyruk {
  gonderimKim: string | null;
  gonderimTs: string | null;
  raporVar: boolean;
}

/** Onay penceresinin kuyruk cümlesi: riser kuyruktan çıkar; Coating ya da Assembly raporu yüklüyse kuyrukta kalır. Kuyrukta değilse null. */
export function ekPencereKuyrukCumlesi(rn: string, kuyruk: EkKuyruk | null | undefined): string | null {
  if (!kuyruk) return null;
  return kuyruk.raporVar
    ? cevir("R{0} stays in the assembly queue because a Coating or Assembly report is uploaded.", rn)
    : cevir("R{0} leaves the assembly queue now and falls back into it by itself when every component is complete again.", rn);
}

/**
 * Riser bandının kuyruk cümlesi (yalnız riser assembly kuyruğundayken): kim, ne zaman gönderdi ve açınca kuyruktan çıkacağı.
 * Rapor yüklüyse açmak riseri kuyruktan ÇIKARMAZ; bant o zaman pencerenin "stays in the assembly queue" cümlesini yazar.
 */
export function ekBantKuyrukCumlesi(rn: string, kuyruk: EkKuyruk | null | undefined, tarih: (iso: string | null) => string): string | null {
  if (!kuyruk) return null;
  if (kuyruk.raporVar) return ekPencereKuyrukCumlesi(rn, kuyruk);
  return cevir("R{0} is in the assembly queue (sent by {1} · {2}); opening takes it out of the queue until the Item is sent to Repair planning.", rn, kuyruk.gonderimKim ?? "—", tarih(kuyruk.gonderimTs));
}

/** Onay penceresi: satırı açılacak, daha önce assembly'e aktarılmış komponent başına bir cümle (komponent sırasında, birer kez). */
export const ekPencereAktarilmisCumleleri = (satirlar: readonly EkAcikSatir[]): string[] =>
  [...new Set(satirlar.filter((s) => s.aktarildi).map((s) => s.komponent))].map((ad) =>
    cevir("{0} is already transferred to assembly; it is open again until the new Item is sent to Repair planning.", ad),
  );

/** `POST /api/muayene/:rn/ek-reject` yanıtı. `st` dolu = rota saklı rework girdisinin beyanından; null = route card ekrandan seçilecek. */
export interface EkRejectYaniti {
  ok: boolean;
  acilan: { kod: string; komponent: string; no: number; ad: string; st: string | null }[];
  kuyruk: "cikti" | "rapor" | "yok";
  loglar: { id: string; ts: string; kim: string; mesaj: string }[];
}

/** Sonuç bandının satırları: açılan her kalem için bir satır. */
export const ekSonucSatirlari = (acilan: EkRejectYaniti["acilan"]): string[] =>
  acilan.map((a) =>
    a.st
      ? cevir("{0}: {1} opened as #{2} · route {3} (from the rework input)", a.komponent, a.ad, a.no, a.st)
      : cevir("{0}: {1} opened as #{2} · route card to be chosen", a.komponent, a.ad, a.no),
  );

/**
 * Sunucunun rework yükleme notu mu: "n ADDITIONAL REJECT row opens as a Replacement Item in Item level Triage: …" (rework-isle.service.ts
 * `ekRejectNotu`). Bu not riserin sayfasındaki "note waiting" listesine YAZILMAZ (bulgu artık bir kalemdir); toplu pencere onu
 * "notes are listed on the riser's page" sayısına katmaz, kendi satırında gösterir.
 */
export const ekRejectNotuMu = (metin: string): boolean =>
  /^\d+ ADDITIONAL REJECT (row opens as a Replacement Item|rows open as Replacement Items) in Item level Triage: /.test(metin);
